import { useEffect, useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const initials = (name) => name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// One person on the stage. `mouseX` is the pointer position (px) relative to the stage track.
export const PersonTile = ({ person, index, mouseX, hovered, anyHovered, onHover, onOpen }) => {
  const { t, lang } = useLang();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const center = useMotionValue(0);

  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (el) center.set(el.offsetLeft + el.offsetWidth / 2);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [center]);

  // Subtle perspective: tiles turn toward the cursor and lift as it gets closer.
  const rotateY = useSpring(
    useTransform([mouseX, center], ([mx, c]) => (reduce || mx === null ? 0 : Math.max(-6, Math.min(6, (mx - c) / 60)))),
    { stiffness: 120, damping: 20 }
  );
  const z = useSpring(
    useTransform([mouseX, center], ([mx, c]) => (reduce || mx === null ? 0 : Math.max(0, 30 - Math.abs(mx - c) / 8))),
    { stiffness: 120, damping: 20 }
  );

  const isHost = person.relationshipType === "host";
  const dimmed = anyHovered && !hovered;

  return (
    <motion.li
      ref={ref}
      data-testid={`person-tile-${person.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: 0.06 * index, ease: [0.22, 1, 0.36, 1] }}
      className="relative -ml-px h-full shrink-0 snap-center first:ml-0"
      style={{ transformStyle: "preserve-3d" }}
    >
      <motion.button
        type="button"
        onMouseEnter={() => onHover(person.id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(person.id)}
        onBlur={() => onHover(null)}
        onClick={() => onOpen(person)}
        aria-label={`${person.name[lang]} — ${person.profession[lang]}. ${t.people.openProfile}`}
        data-testid={`person-button-${person.id}`}
        style={{ rotateY, z, transformStyle: "preserve-3d" }}
        animate={{ scale: hovered ? 1.06 : 1, y: hovered ? -12 : 0, filter: dimmed ? "brightness(0.4) saturate(0.7)" : "brightness(1) saturate(1)" }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="group relative block h-full cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ember/70"
      >
        {person.portrait ? (
          <img
            src={person.portrait}
            alt={person.name[lang]}
            loading="lazy"
            draggable="false"
            className="h-full w-auto select-none"
            style={{ filter: hovered ? "drop-shadow(0 0 28px rgba(241,90,36,0.35))" : "none", transition: "filter 0.5s ease" }}
          />
        ) : (
          <div
            data-testid={`person-monogram-${person.id}`}
            className="flex h-full w-[150px] flex-col items-center justify-center bg-black md:w-[170px]"
          >
            <span className="flex h-24 w-24 items-center justify-center rounded-full border border-ember/50 font-editorial text-3xl italic text-ember md:h-28 md:w-28 md:text-4xl">
              {initials(person.name.en)}
            </span>
            <span className="mt-6 px-4 text-center text-[10px] font-semibold uppercase tracking-[0.25em] text-ash">
              {t.people.relationship[person.relationshipType]}
            </span>
          </div>
        )}

        {isHost && (
          <span className="absolute left-1/2 top-[4%] -translate-x-1/2 whitespace-nowrap rounded-full border border-ember/60 bg-black/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-ember backdrop-blur">
            {t.people.relationship.host}
          </span>
        )}

        {/* Name / role card — appears on hover (desktop) or stays visible on touch devices */}
        <span
          className={`pointer-events-none absolute left-1/2 top-[71%] z-20 w-max max-w-[240px] -translate-x-1/2 border border-line/80 bg-black/85 px-4 py-3 text-left backdrop-blur-md transition-all duration-500 [@media(hover:none)]:max-w-[112px] [@media(hover:none)]:translate-y-0 [@media(hover:none)]:px-2 [@media(hover:none)]:py-2 [@media(hover:none)]:text-center [@media(hover:none)]:opacity-100 ${
            hovered ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <span className="block font-display text-sm font-bold leading-tight text-cream md:text-base [@media(hover:none)]:text-xs">{person.name[lang]}</span>
          <span className="mt-1 block text-[11px] leading-snug text-ash [@media(hover:none)]:hidden">{person.profession[lang]}</span>
          <span className="mt-2 block text-[10px] font-semibold uppercase tracking-[0.22em] text-ember [@media(hover:none)]:hidden">{t.people.openProfile} →</span>
        </span>
      </motion.button>
    </motion.li>
  );
};
